import React from 'react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { TripDetails, TripSegment, TripStop } from '../../types/trip-details';

const id = () =>
  globalThis.crypto?.randomUUID?.() ||
  `trip-${Date.now()}-${Math.random().toString(16).slice(2)}`;
const emptyStop = (kind: TripStop['kind'] = 'stop'): TripStop => ({
  id: id(),
  label: '',
  kind,
  address: { line1: '' },
});
function move<T>(items: T[], from: number, direction: number) {
  const next = [...items],
    to = from + direction;
  if (to < 0 || to >= next.length) return next;
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}
function IconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded border border-outline/50 disabled:opacity-30"
    >
      {children}
    </button>
  );
}
function StopsEditor({
  value,
  onChange,
}: {
  value: TripStop[];
  onChange: (stops: TripStop[]) => void;
}) {
  const update = (index: number, patch: Partial<TripStop>) =>
    onChange(
      value.map((stop, i) => (i === index ? { ...stop, ...patch } : stop))
    );
  return (
    <div className="space-y-2">
      {value.map((stop, index) => (
        <div
          key={stop.id || index}
          className="grid grid-cols-1 gap-2 border-t border-outline/40 py-3 sm:grid-cols-2"
        >
          <label className="text-xs">
            Stop name
            <input
              className="input-field"
              value={stop.label || ''}
              onChange={(event) =>
                update(index, {
                  id: stop.id || id(),
                  label: event.target.value,
                })
              }
            />
          </label>
          <label className="text-xs">
            Address
            <input
              className="input-field"
              value={
                stop.address?.line1 ||
                stop.address?.formatted ||
                (stop as any).location ||
                ''
              }
              onChange={(event) =>
                update(index, {
                  id: stop.id || id(),
                  address: { ...stop.address, line1: event.target.value },
                })
              }
            />
          </label>
          <label className="text-xs">
            Stop type
            <select
              className="input-field"
              value={stop.kind || 'stop'}
              onChange={(event) =>
                update(index, { kind: event.target.value as TripStop['kind'] })
              }
            >
              <option value="pickup">Pickup</option>
              <option value="stop">Stop</option>
              <option value="dropoff">Dropoff</option>
            </select>
          </label>
          <label className="text-xs">
            Planned arrival (ISO offset)
            <input
              className="input-field"
              value={stop.planned_at || ''}
              placeholder="2026-11-01T09:00:00-05:00"
              onChange={(event) =>
                update(index, { planned_at: event.target.value || null })
              }
            />
          </label>
          <div className="flex gap-2 sm:col-span-2">
            <IconButton
              label="Move stop up"
              disabled={index === 0}
              onClick={() => onChange(move(value, index, -1))}
            >
              <ArrowUpIcon className="h-4 w-4" />
            </IconButton>
            <IconButton
              label="Move stop down"
              disabled={index === value.length - 1}
              onClick={() => onChange(move(value, index, 1))}
            >
              <ArrowDownIcon className="h-4 w-4" />
            </IconButton>
            <IconButton
              label="Remove stop"
              onClick={() => onChange(value.filter((_, i) => i !== index))}
            >
              <TrashIcon className="h-4 w-4" />
            </IconButton>
          </div>
        </div>
      ))}
      <button
        type="button"
        className="btn-secondary inline-flex items-center gap-2 text-sm"
        onClick={() => onChange([...value, emptyStop()])}
      >
        <PlusIcon className="h-4 w-4" />
        Add stop
      </button>
    </div>
  );
}

export default function TripDetailsEditor({
  value,
  onChange,
  commercialLocked = false,
}: {
  value: TripDetails;
  onChange: (patch: Partial<TripDetails>) => void;
  commercialLocked?: boolean;
}) {
  const segments = value.segments || [];
  const updateSegment = (index: number, patch: Partial<TripSegment>) =>
    onChange({
      segments: segments.map((segment, i) =>
        i === index ? { ...segment, ...patch } : segment
      ),
    });
  return (
    <section className="space-y-4 border-t border-outline/50 pt-4">
      <h3 className="text-sm font-semibold">Trip plan and operations</h3>
      <fieldset
        disabled={commercialLocked}
        className="space-y-4 disabled:opacity-60"
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="text-xs">
            Passengers
            <input
              type="number"
              min="1"
              max="10000"
              className="input-field"
              value={value.passenger_count ?? ''}
              onChange={(event) =>
                onChange({
                  passenger_count: event.target.value
                    ? Number(event.target.value)
                    : null,
                })
              }
            />
          </label>
          <label className="text-xs">
            Service timezone
            <input
              className="input-field"
              value={value.timezone || ''}
              placeholder="America/New_York"
              onChange={(event) => onChange({ timezone: event.target.value })}
            />
          </label>
        </div>
        <h4 className="text-xs font-semibold">Ordered stops</h4>
        <StopsEditor
          value={value.stops || []}
          onChange={(stops) => onChange({ stops })}
        />
        <h4 className="text-xs font-semibold">Separate trips</h4>
        {segments.map((segment, index) => (
          <div
            key={segment.id}
            className="space-y-3 border-t border-outline/50 pt-3"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="text-xs">
                Trip name
                <input
                  className="input-field"
                  value={segment.label}
                  onChange={(event) =>
                    updateSegment(index, { label: event.target.value })
                  }
                />
              </label>
              <label className="text-xs">
                Timezone
                <input
                  className="input-field"
                  value={segment.timezone}
                  onChange={(event) =>
                    updateSegment(index, { timezone: event.target.value })
                  }
                />
              </label>
              <label className="text-xs">
                Start (ISO offset)
                <input
                  className="input-field"
                  value={segment.start_at}
                  onChange={(event) =>
                    updateSegment(index, { start_at: event.target.value })
                  }
                />
              </label>
              <label className="text-xs">
                End (ISO offset)
                <input
                  className="input-field"
                  value={segment.end_at}
                  onChange={(event) =>
                    updateSegment(index, { end_at: event.target.value })
                  }
                />
              </label>
              <label className="text-xs">
                Trip passengers
                <input
                  type="number"
                  min="1"
                  className="input-field"
                  value={segment.passenger_count ?? ''}
                  onChange={(event) =>
                    updateSegment(index, {
                      passenger_count: event.target.value
                        ? Number(event.target.value)
                        : null,
                    })
                  }
                />
              </label>
            </div>
            <StopsEditor
              value={segment.stops}
              onChange={(stops) => updateSegment(index, { stops })}
            />
            <div className="flex gap-2">
              <IconButton
                label="Move trip up"
                disabled={index === 0}
                onClick={() =>
                  onChange({ segments: move(segments, index, -1) })
                }
              >
                <ArrowUpIcon className="h-4 w-4" />
              </IconButton>
              <IconButton
                label="Move trip down"
                disabled={index === segments.length - 1}
                onClick={() => onChange({ segments: move(segments, index, 1) })}
              >
                <ArrowDownIcon className="h-4 w-4" />
              </IconButton>
              <IconButton
                label="Remove trip"
                onClick={() =>
                  onChange({ segments: segments.filter((_, i) => i !== index) })
                }
              >
                <TrashIcon className="h-4 w-4" />
              </IconButton>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="btn-secondary inline-flex items-center gap-2 text-sm"
          onClick={() =>
            onChange({
              segments: [
                ...segments,
                {
                  id: id(),
                  label: '',
                  start_at: '',
                  end_at: '',
                  timezone: value.timezone || 'America/New_York',
                  stops: [emptyStop('pickup'), emptyStop('dropoff')],
                },
              ],
            })
          }
        >
          <PlusIcon className="h-4 w-4" />
          Add trip
        </button>
      </fieldset>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {(['actual_pickup_at', 'actual_dropoff_at'] as const).map((key) => (
          <label className="text-xs" key={key}>
            {key === 'actual_pickup_at'
              ? 'Actual pickup (ISO offset)'
              : 'Actual dropoff (ISO offset)'}
            <input
              className="input-field"
              value={value[key] || ''}
              onChange={(event) =>
                onChange({ [key]: event.target.value || null })
              }
            />
          </label>
        ))}
      </div>
      {(
        ['passenger_instructions', 'dispatch_notes', 'internal_notes'] as const
      ).map((key) => (
        <label className="block text-xs" key={key}>
          {key === 'passenger_instructions'
            ? 'Passenger instructions'
            : key === 'dispatch_notes'
            ? 'Dispatch notes'
            : 'Operator internal notes'}
          <textarea
            className="input-field mt-1 min-h-[72px]"
            maxLength={10000}
            value={value[key] || ''}
            onChange={(event) =>
              onChange({ [key]: event.target.value || null })
            }
          />
        </label>
      ))}
    </section>
  );
}
